import { ApiClient } from '../../../../infra';
import { fromBackend } from '../../../../domain';
/**
 * A administracao de usuarios da empresa, e a fila de solicitacoes.
 *
 * O `companyId` diz QUAL empresa: a escolhida pelo administrador geral, ou a do
 * cabecalho para quem administra varias. O servidor confere que quem pede administra
 * a empresa pedida; sem o parametro, ele so deduz quando ha uma empresa so.
 */
export class CompanyUsersApi {
    constructor() {
        this.client = ApiClient.INSTANCE;
    }
    static { this.INSTANCE = new CompanyUsersApi(); }
    async fetchUsers(companyId) {
        const users = (await this.client.send('GET', `/company/users${this.scope(companyId)}`));
        // O ultimo acesso e um instante: a coluna e o filtro de "acessou nos ultimos dias" o
        // leem no fuso de quem olha.
        return users.map((user) => ({ ...user, lastAccessAt: fromBackend(user.lastAccessAt) }));
    }
    async fetchPending(companyId) {
        const requests = (await this.client.send('GET', `/company/links/pending${this.scope(companyId)}`));
        return requests.map((request) => ({ ...request, requestedAt: fromBackend(request.requestedAt) }));
    }
    /**
     * Procura alguem que ja esteja na plataforma, pelo e-mail.
     *
     * `undefined` quando nao ha ninguem - o servidor responde 204, e "nao existe" e
     * resposta e nao erro.
     *
     * O PAPEL vai junto porque "ja esta nesta empresa" so quer dizer algo dentro de um:
     * quem atende a empresa como profissional ainda pode passar a administra-la.
     */
    async lookup(email, professional, companyId) {
        const escopo = this.scope(companyId);
        const junta = escopo ? '&' : '?';
        const query = `${escopo}${junta}email=${encodeURIComponent(email)}&profissional=${professional}`;
        return (await this.client.send('GET', `/company/users/lookup${query}`));
    }
    /**
     * Quem ja esta na plataforma e pode ser vinculado a esta empresa.
     *
     * Sem termo, sao as pessoas das OUTRAS empresas de quem pede - o caso que
     * motivou tudo isso. Com termo, elas filtradas mais quem for achado pelo e-mail
     * ou pelo CPF inteiros.
     */
    async fetchCandidates(term, professional, companyId) {
        const escopo = this.scope(companyId);
        const junta = escopo ? '&' : '?';
        const busca = term.trim() ? `&term=${encodeURIComponent(term.trim())}` : '';
        return (await this.client.send('GET', `/company/users/candidates${escopo}${junta}profissional=${professional}${busca}`));
    }
    /**
     * Vincula a esta empresa alguem que JA esta na plataforma.
     *
     * Dois campos, e nao um cadastro: vincular nao e cadastrar. O CPF e a prova de
     * identidade - sem ele bastaria saber o e-mail de alguem para lhe dar um papel.
     */
    async link(input, companyId) {
        await this.client.send('POST', `/company/users/link${this.scope(companyId)}`, input);
    }
    async addAdmin(input, companyId) {
        await this.client.send('POST', `/company/users/admin${this.scope(companyId)}`, input);
    }
    async addProfessional(input, companyId) {
        await this.client.send('POST', `/company/users/professional${this.scope(companyId)}`, input);
    }
    /**
     * As profissoes de saude.
     *
     * Endpoint PUBLICO, e nao por descuido: ele so lista os 55 conselhos, que sao
     * dado de referencia e nao de ninguem. Era do auto cadastro, que saiu; quem o
     * le hoje e o cadastro de profissional pela empresa.
     */
    async fetchProfessionalTypes() {
        return (await this.client.send('GET', '/professional_type/all'));
    }
    /** Os colaboradores da empresa, marcando quais este profissional acompanha. */
    async fetchCollaborators(professionalId, companyId) {
        return (await this.client.send('GET', `/company/users/${professionalId}/patients${this.scope(companyId)}`));
    }
    async assign(professionalId, patientId, assign, companyId) {
        await this.client.send(assign ? 'POST' : 'DELETE', `/company/users/${professionalId}/patients/${patientId}${this.scope(companyId)}`);
    }
    /** Tudo o que a empresa configura de um usuario dela. */
    async fetchUser(userId, companyId) {
        return (await this.client.send('GET', `/company/users/${userId}${this.scope(companyId)}`));
    }
    async updateUser(userId, input, companyId) {
        await this.client.send('PATCH', `/company/users/${userId}${this.scope(companyId)}`, input);
    }
    /**
     * Liga e desliga o acesso.
     *
     * Este caminho tem ESCOPO - so alcanca quem e da empresa. O `/user/activate` do
     * legado alcanca qualquer conta por id, e por isso ficou restrito ao
     * administrador geral.
     */
    async setActive(userId, active, companyId) {
        const escopo = this.scope(companyId);
        const junta = escopo ? '&' : '?';
        await this.client.send('PATCH', `/company/users/${userId}/active${escopo}${junta}active=${active}`);
    }
    async updateCpf(userId, cpf, companyId) {
        await this.client.send('POST', `/company/users/${userId}/cpf${this.scope(companyId)}`, { cpf });
    }
    /**
     * Torna este administrador o MASTER da empresa (B-30). Quem era perde a condicao no
     * mesmo ato - e o servidor so aceita do master atual ou da plataforma.
     */
    async transferMaster(userId, companyId) {
        await this.client.send('POST', `/company/users/${userId}/master${this.scope(companyId)}`);
    }
    async removeUser(userId, companyId) {
        await this.client.send('DELETE', `/company/users/${userId}${this.scope(companyId)}`);
    }
    async approve(requestId, companyId) {
        await this.client.send('POST', `/company/links/${requestId}/approve${this.scope(companyId)}`);
    }
    async reject(requestId, companyId) {
        await this.client.send('POST', `/company/links/${requestId}/reject${this.scope(companyId)}`);
    }
    scope(companyId) {
        return companyId ? `?companyId=${companyId}` : '';
    }
}
//# sourceMappingURL=company-users.api.js.map